import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Modal } from '../../Shared/Modal';
import { Button } from '../../Shared/Button';
import { TaskChecklistPanel } from './TaskChecklistPanel';
import type { OpenChecklistDayGroup } from '../../../store/useMyChecklistStore';
import { formatDate, parseIsoDate } from '../../../utils/dateUtils';

interface ChecklistClockOutDialogProps {
  isOpen: boolean;
  /** The employee's current day — every other group is a past day and gets a date heading. */
  todayIso: string;
  dayGroups: OpenChecklistDayGroup[];
  busy?: boolean;
  /** Close without clocking out — the employee wants to finish the checklists first. */
  onCancel: () => void;
  /** Clock out regardless; the open items stay open in the protocol. */
  onClockOutAnyway: () => void;
}

function countOpen(groups: OpenChecklistDayGroup[]): number {
  return groups.reduce(
    (sum, group) =>
      sum +
      group.tasks.reduce(
        (taskSum, task) => taskSum + (task.items ?? []).filter((i) => !i.isCompleted).length,
        0,
      ),
    0,
  );
}

/**
 * Shown when an employee clocks out with checklist items still unconfirmed — today's or any
 * left over from an earlier day.
 * why: warns, never blocks — the time clock carries wage-relevant data and must not depend on
 * checklist state (ADR-0010). Items can be ticked off right here, on the day they belong to.
 */
export function ChecklistClockOutDialog({
  isOpen,
  todayIso,
  dayGroups,
  busy = false,
  onCancel,
  onClockOutAnyway,
}: ChecklistClockOutDialogProps) {
  const { t } = useTranslation('dashboard');

  const openCount = countOpen(dayGroups);
  const pastCount = countOpen(dayGroups.filter((group) => group.date !== todayIso));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={t('clockoutDialog.title')}
      size={dayGroups.length > 1 ? 'lg' : 'md'}
      footer={
        <div className="flex justify-end gap-2">
          {openCount > 0 && (
            <Button variant="secondary" onClick={onCancel} disabled={busy}>
              {t('clockoutDialog.finishFirst')}
            </Button>
          )}
          <Button onClick={onClockOutAnyway} disabled={busy}>
            {openCount > 0 ? t('clockoutDialog.clockOutAnyway') : t('clockoutDialog.clockOut')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {openCount > 0 ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800 p-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <div className="text-[13px] text-amber-900 dark:text-amber-200">
              <p>{t('clockoutDialog.message', { count: openCount })}</p>
              {pastCount > 0 && (
                <p className="mt-0.5">{t('clockoutDialog.pastDays', { count: pastCount })}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/10 dark:border-emerald-800 p-3">
            <p className="text-[13px] text-emerald-900 dark:text-emerald-200">
              {t('clockoutDialog.allDone')}
            </p>
          </div>
        )}

        {dayGroups.map((group) => (
          <div key={group.date} className="space-y-2">
            {/* A heading only earns its place once more than one day is on screen. */}
            {dayGroups.length > 1 && (
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                {group.date === todayIso
                  ? t('clockoutDialog.today')
                  : t('clockoutDialog.dayHeading', { date: formatDate(parseIsoDate(group.date)) })}
              </div>
            )}

            {group.tasks.map((task) => (
              <div key={`${group.date}-${task.taskId}`}>
                <div className="text-[13px] font-semibold text-ink dark:text-slate-100">
                  {task.objectName}
                </div>
                <div className="text-[11px] text-ink-muted dark:text-slate-400">{task.taskName}</div>
                <TaskChecklistPanel date={group.date} group={task} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </Modal>
  );
}
